// SW-control recovery. Catches two cases where the page lacks a
// controller after `sw.ready`:
//
//   (a) Hard-reload bypass: Chrome's Ctrl+Shift+R skips the SW for
//       the main document, leaving `navigator.serviceWorker.controller`
//       permanently null for the navigation.
//   (b) First-visit claim race: the SW just registered, ran
//       `clients.claim()` in `activate`, but the page reaches the
//       recovery check before the controller binding propagates.
//       Spec-allowed and routinely observed on cold first-loads.
//
// Without recovery either case dead-ends `installSwHandoff`'s
// `waitForController()` and the iframe never mounts.
//
// Recovery: when the controller is still null after a grace period,
// soft-reload once (loop-guarded by sessionStorage). The active SW
// claims the resulting navigation immediately via `clients.claim()`,
// and the user sees a brief flash rather than a blank page. A real
// scope-mismatch deploy bug trips the loop guard on the second pass
// and surfaces a clear error instead of spinning.
//
// This is NOT the fix for ordinary SW respawns — the controllerchange
// re-handoff path has a real controller and just needs a new port.
// This is the "no controller at all" path.

import type { SwContainerLike } from "./sw-handoff.js";

/** Outcome of `ensureSwControllable`. */
export type EnsureSwControllableResult =
  | { kind: "controlled" }
  | { kind: "reloading" }
  | { kind: "uncontrolled"; reason: UncontrolledReason };

export type UncontrolledReason =
  | "sw_ready_timeout"
  | "loop_guard_tripped"
  | "no_session_storage"
  | "session_storage_failed";

/** Subset of `sessionStorage` the recovery helper uses. Trimming to
 *  three calls keeps test fakes trivial and pins the contract. */
export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const RECOVERY_FLAG_KEY = "p2claw_hard_reload_recovery";

/** 5s cap on awaiting `navigator.serviceWorker.ready`. Beyond this,
 *  the install is considered stuck — reloading won't help. */
export const DEFAULT_SW_READY_TIMEOUT_MS = 5_000;

/** 2s grace after `sw.ready` for `controllerchange` to fire. Under a
 *  normal first-visit or reload the claim → controller binding lands
 *  within tens of ms; 2s is generous. */
export const DEFAULT_CONTROLLER_GRACE_MS = 2_000;

export interface EnsureSwControllableOptions {
  navigatorSw: SwContainerLike;
  /** `navigator.serviceWorker.ready` — promise of the first active
   *  registration. Caller passes it explicitly so tests don't need
   *  to monkey-patch the global. */
  swReady: Promise<unknown>;
  /**
   * `sessionStorage` or a compatible stub. Undefined means "no
   * loop-guard available" — recovery is skipped in that case.
   */
  sessionStorage: SessionStorageLike | undefined;
  /** What to do on the recovery path. Swap for a spy in tests. */
  reload(): void;
  /** Logger for diagnostics. Defaults to `console.warn`. */
  logger?(message: string, detail?: Record<string, unknown>): void;
  /** Test seams. */
  setTimeoutFn?(cb: () => void, ms: number): unknown;
  clearTimeoutFn?(id: unknown): void;
  swReadyTimeoutMs?: number;
  controllerGraceMs?: number;
}

/**
 * Recover the page into the "SW is in control" state. Call once
 * during `bootWrapper`, after `registerServiceWorker()` and before
 * `installSwHandoff()`. The return value is the caller's cue to
 * continue the boot flow (`controlled`), abort the current boot
 * (`reloading` — page is about to navigate), or decide how to degrade
 * (`uncontrolled` — recovery declined; reason distinguishes the
 * "couldn't try" cases from the "tried once, it didn't help" case).
 */
export async function ensureSwControllable(
  opts: EnsureSwControllableOptions,
): Promise<EnsureSwControllableResult> {
  const setTimeoutFn =
    opts.setTimeoutFn ?? ((cb: () => void, ms: number) => setTimeout(cb, ms));
  const clearTimeoutFn =
    opts.clearTimeoutFn ??
    ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const readyTimeoutMs = opts.swReadyTimeoutMs ?? DEFAULT_SW_READY_TIMEOUT_MS;
  const graceMs = opts.controllerGraceMs ?? DEFAULT_CONTROLLER_GRACE_MS;
  const log =
    opts.logger ??
    ((msg: string, detail?: Record<string, unknown>) =>
      console.warn(
        `[p2claw:sw-recovery] ${msg}`,
        detail ?? "",
      ));

  // (1) Wait for sw.ready with a hard cap. If it never resolves, the
  // install is stuck; reloading won't help — return early.
  const readyResult = await raceWithTimeout(
    opts.swReady,
    readyTimeoutMs,
    setTimeoutFn,
    clearTimeoutFn,
  );
  if (readyResult === "timeout") {
    log("sw.ready did not resolve within timeout — recovery skipped", {
      timeoutMs: readyTimeoutMs,
    });
    return { kind: "uncontrolled", reason: "sw_ready_timeout" };
  }

  // (2) Controller may still be null even after sw.ready — that's
  // the case we're recovering. Give it a grace period in which
  // `controllerchange` could land (claim binding usually completes
  // within tens of ms once the SW activates).
  if (!opts.navigatorSw.controller) {
    await waitForControllerChange(opts.navigatorSw, graceMs, setTimeoutFn, clearTimeoutFn);
  }

  // (3) Controller landed → happy path. Clear the loop-guard flag so
  // a future recovery attempt in this tab gets a fresh chance.
  if (opts.navigatorSw.controller) {
    safeRemoveItem(opts.sessionStorage, RECOVERY_FLAG_KEY);
    return { kind: "controlled" };
  }

  // (4) Loop guard. All sessionStorage access inside one try/catch —
  // privacy mode / quota full / disabled storage all surface here,
  // and the safe response in every case is "don't reload". A reload
  // we can't loop-guard would spin the tab forever on a pathological
  // SW (real scope mismatch, install failure).
  if (!opts.sessionStorage) {
    log(
      "no sessionStorage available — cannot loop-guard; proceeding uncontrolled",
    );
    return { kind: "uncontrolled", reason: "no_session_storage" };
  }
  try {
    const prior = opts.sessionStorage.getItem(RECOVERY_FLAG_KEY);
    if (prior) {
      log(
        "recovery loop guard tripped — already tried in this tab; proceeding uncontrolled",
      );
      return { kind: "uncontrolled", reason: "loop_guard_tripped" };
    }
    opts.sessionStorage.setItem(RECOVERY_FLAG_KEY, "1");
  } catch (e) {
    log(
      "sessionStorage access threw — cannot loop-guard; proceeding uncontrolled",
      { error: (e as Error).message ?? String(e) },
    );
    return { kind: "uncontrolled", reason: "session_storage_failed" };
  }

  // (5) Soft reload. The active SW will claim the resulting page
  // load — `kind === "reloading"` tells the caller to stop boot NOW;
  // the next page load resumes from step 1 with a controller set,
  // the flag clears (step 3), and the user sees a brief flash rather
  // than a blank page.
  log("no controller after grace; soft-reloading to re-establish SW control");
  opts.reload();
  return { kind: "reloading" };
}

/**
 * Read `sessionStorage` safely. Returns `undefined` when the host is
 * running under a strict-privacy configuration (Safari private mode,
 * iframe `sandbox` without `allow-same-origin`, operator-disabled
 * storage) so callers can route to the "no loop guard" path without
 * crashing boot. Exported for the wrapper-side glue.
 */
export function safeGetSessionStorage(): SessionStorageLike | undefined {
  try {
    const s = globalThis.sessionStorage;
    if (!s) return undefined;
    // Probe a write — some browsers return a `Storage` object but
    // throw on any mutation (privacy mode). Clean up immediately.
    const probe = "__p2claw_probe__";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return undefined;
  }
}

// ---------- internal helpers ----------

/**
 * Race a promise against a timer; returns the promise's value on
 * resolution, `"timeout"` if the timer fires first, or rejects if the
 * promise rejects. Rejection of `promise` propagates (callers need to
 * distinguish "SW install errored" from "SW install is slow"; the
 * brief treats both as "recovery not possible, return early", so we
 * translate a rejected `swReady` into a `"timeout"` sentinel).
 */
async function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
  setTimeoutFn: (cb: () => void, ms: number) => unknown,
  clearTimeoutFn: (id: unknown) => void,
): Promise<T | "timeout"> {
  let timerId: unknown;
  const timer = new Promise<"timeout">((resolve) => {
    timerId = setTimeoutFn(() => resolve("timeout"), ms);
  });
  try {
    const winner = await Promise.race([
      promise.then(
        (v) => ({ kind: "ok" as const, value: v }),
        () => ({ kind: "err" as const }),
      ),
      timer.then(() => ({ kind: "timeout" as const })),
    ]);
    if (winner.kind === "ok") return winner.value;
    // Rejection and timeout both surface as "cannot recover".
    return "timeout";
  } finally {
    clearTimeoutFn(timerId);
  }
}

/**
 * Resolve when `controllerchange` fires on `navigatorSw`, or when the
 * timeout elapses — whichever is first. Always resolves (never
 * rejects). The caller checks `navigatorSw.controller` itself to
 * decide whether the race was productive.
 */
function waitForControllerChange(
  navigatorSw: SwContainerLike,
  ms: number,
  setTimeoutFn: (cb: () => void, ms: number) => unknown,
  clearTimeoutFn: (id: unknown) => void,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      navigatorSw.removeEventListener("controllerchange", onChange);
      clearTimeoutFn(timerId);
      resolve();
    };
    const onChange = (): void => finish();
    navigatorSw.addEventListener("controllerchange", onChange);
    const timerId = setTimeoutFn(finish, ms);
  });
}

function safeRemoveItem(
  storage: SessionStorageLike | undefined,
  key: string,
): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* ignore — flag clear is best-effort */
  }
}
